/**
 * Заявка компании на проведение акций.
 *
 * Публичной регистрации у кабинета нет и не будет: за каждым брендом стоит
 * договорённость с живым человеком. Раньше тот, кто пришёл с той стороны,
 * упирался в строчку «напиши нам на почту» — то есть в пустоту, из которой не
 * видно ни кто написал, ни ответили ли ему.
 *
 * Эта форма ничего не создаёт и никому не открывает доступ. Она делает ровно
 * одно: превращает «напиши нам» в начало, которое видно обеим сторонам.
 * Поэтому и сказано об этом прямо — обещать кабинет по нажатию кнопки было бы
 * враньём.
 */
import { ApiError } from '@allswipes/client';
import { useT } from '@allswipes/i18n';
import { Button, Card, Field, REGIONS } from '@allswipes/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api';
import { Shell } from '../components/Shell';

export function CompanyRequestScreen() {
  const t = useT();
  const [form, setForm] = useState({
    company: '',
    name: '',
    surname: '',
    email: '',
    phone: '',
    region: 'TJ',
    about: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.requestCompanyAccess(form);
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Не получилось отправить заявку'));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <Shell title={t('Заявка отправлена')}>
        <Card>
          <p className="sub" style={{ marginBottom: 10 }}>
            {t('Получили. Ответим на {email} в ближайшие дни — письмом от живого человека, а не автоматом.', { email: form.email })}
          </p>
          <p className="sub" style={{ marginBottom: 14 }}>
            {t('Кабинет откроется по одноразовой ссылке: пароль ты придумаешь сам, и его не будем знать ни мы, ни кто-либо ещё.')}
          </p>
          <Link to="/" className="btn out block">
            {t('Вернуться к акциям')}
          </Link>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell title={t('Компании')}>
      <Card>
        <p className="sub" style={{ marginBottom: 6 }}>
          {t('Проводить акции на платформе может компания, с которой мы договорились. Поэтому кабинет не заводится по кнопке — расскажи о себе, и мы ответим.')}
        </p>
        <p className="sub" style={{ marginBottom: 16 }}>
          {t('Это не регистрация и не очередь: мы читаем каждую заявку сами.')}
        </p>

        <Field label={t('Компания')}>
          <input
            className="input"
            placeholder="ООО «Ойла»"
            value={form.company}
            onChange={(e) => set({ company: e.target.value })}
          />
        </Field>

        <div className="grid c2">
          <Field label={t('Имя')}>
            <input
              className="input"
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
            />
          </Field>
          <Field label={t('Фамилия')}>
            <input
              className="input"
              value={form.surname}
              onChange={(e) => set({ surname: e.target.value })}
            />
          </Field>
        </div>

        <Field label={t('Рабочая почта')} error={error ?? undefined}>
          <input
            className="input"
            type="email"
            inputMode="email"
            placeholder="promo@oila.tj"
            value={form.email}
            onChange={(e) => set({ email: e.target.value })}
          />
        </Field>

        <div className="grid c2">
          <Field label={t('Телефон')}>
            <input
              className="input"
              value={form.phone}
              onChange={(e) => set({ phone: e.target.value })}
            />
          </Field>
          <Field label={t('Страна')}>
            <select
              className="select"
              value={form.region}
              onChange={(e) => set({ region: e.target.value })}
            >
              {REGIONS.map((r) => (
                <option key={r.code} value={r.code}>
                  {t(r.name)}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label={t('Чем занимаетесь и какую акцию хотите провести')}>
          <textarea
            className="input"
            rows={3}
            value={form.about}
            onChange={(e) => set({ about: e.target.value })}
          />
        </Field>

        <Button
          variant="pri"
          block
          disabled={busy || !form.company.trim() || !form.name.trim() || !form.email.includes('@')}
          onClick={() => void submit()}
        >
          {busy ? t('Отправляем…') : t('Отправить заявку')}
        </Button>
      </Card>
    </Shell>
  );
}
